import { Search, FileSignature, AlertCircle, Eye, Shield, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  {
    icon: Search,
    title: "Comprehensive Trademark Search",
    description:
      "Before filing your trademark application, our team conducts an exhaustive search of the USPTO database, state trademark registries, common law sources, and business directories to identify potential conflicts. Our comprehensive search includes direct-hit analysis, phonetic equivalents, visual similarities, and conceptual matches. This thorough pre-filing search helps you avoid costly refusals and ensures your chosen mark has the highest possible chance of successful registration. We provide a detailed search report with our findings and recommendations.",
    span: "md:col-span-7",
  },
  {
    icon: FileSignature,
    title: "USPTO Application Filing",
    description:
      "Our experienced trademark professionals prepare and file your trademark application with the United States Patent and Trademark Office with precision and attention to detail. We handle the proper classification of goods and services under the Nice Classification system, draft a clear and defensible description of your mark, and ensure all required documentation is complete and accurate before submission. Our meticulous filing process minimizes the risk of office actions and delays, helping your application move through the USPTO examination process as smoothly as possible.",
    span: "md:col-span-5",
  },
  {
    icon: AlertCircle,
    title: "Office Action Responses",
    description:
      "If the USPTO examiner issues an office action regarding your trademark application, our team will prepare a comprehensive and well-reasoned response. Whether the issue involves a likelihood of confusion with an existing mark, a descriptiveness refusal, or a technical deficiency, we craft persuasive legal arguments supported by relevant case law and evidence to overcome the examiner's objections. Our high success rate in resolving office actions demonstrates our deep understanding of USPTO procedures and trademark law.",
    span: "md:col-span-5",
  },
  {
    icon: Eye,
    title: "Trademark Monitoring",
    description:
      "Protecting your trademark does not end with registration. Our ongoing monitoring service tracks the USPTO database, state registries, and online marketplaces for any new applications or uses that may conflict with your registered mark. When we detect a potentially infringing mark, we promptly notify you and provide actionable recommendations for enforcement, including cease and desist letters and opposition proceedings. Continuous monitoring ensures your brand remains protected against unauthorized use and dilution.",
    span: "md:col-span-7",
  },
  {
    icon: Shield,
    title: "Brand Protection Strategy",
    description:
      "Our brand protection services go beyond simple registration. We work with you to develop a comprehensive intellectual property strategy that safeguards your brand across all channels and jurisdictions. This includes advising on proper trademark use guidelines, helping you maintain your registration through timely filings of declarations of use and renewal applications, and identifying opportunities to expand your trademark portfolio as your business grows. Our proactive approach ensures your brand assets are fully protected and optimized for maximum value.",
    span: "md:col-span-6",
  },
  {
    icon: FileCheck,
    title: "Complete Documentation & Filing",
    description:
      "We provide complete documentation services throughout the entire trademark registration process. From preparing your initial application to organizing post-registration maintenance filings, our team ensures every document is accurate, complete, and filed on time. You receive digital copies of all filings, correspondence with the USPTO, and your official registration certificate. Our systematic approach to documentation means you always have a clear record of your trademark portfolio and its status, making it easy to manage your intellectual property assets over time.",
    span: "md:col-span-6",
  },
];

export default function ServicesSection({ onOpenForm }) {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
            Our Trademark Services
          </span>
          <h2
            data-testid="services-heading"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4"
          >
            Comprehensive Trademark Registration Services
          </h2>
          <p className="text-[#475569] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            From initial trademark search to post-registration monitoring, we provide end-to-end services designed to protect your brand at every stage of the trademark lifecycle. Our specialized team handles every aspect of the USPTO filing process with expertise and precision.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((svc, i) => (
            <div
              key={svc.title}
              data-testid={`service-card-${i}`}
              className={`${svc.span} bg-[#F8F9FA] border border-slate-200 rounded-lg p-8 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group`}
            >
              <div className="w-11 h-11 rounded-lg bg-[#1E3A8A] flex items-center justify-center mb-5 group-hover:bg-[#2563EB] transition-colors">
                <svc.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-[#0F172A] mb-3">
                {svc.title}
              </h3>
              <p className="text-[#475569] text-sm leading-relaxed">{svc.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={onOpenForm}
            data-testid="services-cta-btn"
            className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-8 font-semibold rounded-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            Get Started Today
          </Button>
        </div>
      </div>
    </section>
  );
}
